import { useDeviceType } from '@cars4ever/public-ui';
import { ReactElement, ReactNode, useEffect } from 'react';

import ExternalEvent from '^enums/ExternalEvent';
import ApplicationConfig from '^services/ApplicationConfig';
import IChildHeightChangeEventPayload from '^types/externalEvents/IChildHeightChangeEventPayload';

import styles from './styles.module.scss';

const BACKGROUND_COLOR_CSS_VARIABLE_NAME = '--background-color';
const CHILD_HEIGHT_CHANGE_EVENT_TARGET_ORIGIN = '*';

export interface ILayoutProviderProps {
  children: ReactNode;
}

const LayoutProvider = ({ children }: ILayoutProviderProps): ReactElement => {
  const { isDesktop } = useDeviceType();

  useEffect(
    (): (() => void) | void => {
      if (!window.xprops.isWidgetMode) {
        return;
      }

      document.documentElement.classList.add(styles.widgetLayout);

      if (isDesktop) {
        return;
      }

      if (typeof window.xprops.autoCalculateSpacers === 'function') {
        void window.xprops.autoCalculateSpacers();
      }
    },
    [isDesktop],
  );

  useEffect(
    (): (() => void) | void => {
      const backgroundColor = ApplicationConfig.backgroundColor;

      if (backgroundColor) {
        document.documentElement.style.setProperty(BACKGROUND_COLOR_CSS_VARIABLE_NAME, backgroundColor);
      }

      if (window.xprops.isWidgetMode) {
        return;
      }

      // Emit height change event in non-widget environment
      const observer = new ResizeObserver((): void => {
        const payload: IChildHeightChangeEventPayload = {
          msgType: ExternalEvent.ChildHeightChanged,
          height: document.body.clientHeight,
        };

        window.parent.postMessage(payload, CHILD_HEIGHT_CHANGE_EVENT_TARGET_ORIGIN);
      });

      observer.observe(document.body);

      return (): void => observer.disconnect();
    },
    [],
  );

  return (
    <div className={styles.root}>
      {children}
    </div>
  );
};

export default LayoutProvider;
